import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';

import cannot from 'ember-can/helpers/cannot';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';

import Component from 'umg-ember-backend/components/record-detail/forms';
import Content from 'umg-ember-backend/components/record-detail/forms/story/content';
import ShopList from 'umg-ember-backend/components/shop-list';
import Button from 'umg-ember-backend/components/ui/button';
import Form from 'umg-ember-backend/components/ui/form';

export default class RecordDetailFormsStoryComponent extends Component {
  // Services
  @service confirm;
  @service flashMessages;
  @service permission;
  @service recordStatus;
  @service store;

  // Getter and setter
  get permissonToCreateMlp() {
    let hasPermission = this.permission.userRoles.find(
      (userRole) => userRole.id === '19',
    );

    return hasPermission || this.permission.user.isAdmin;
  }

  get isDisabled() {
    return this.recordStatus.isDisabled(this.args.model);
  }

  // Functions
  createMlp = task({ restartable: true }, async () => {
    try {
      let scope = 'confirm.create_mlp_from_story';
      let ok = await this.confirm.ask(`${scope}.title`, `${scope}.message`);

      if (ok) {
        let mlp = this.store.createRecord('mlp', {
          headline: `${this.args.model.headline} (Aus Story erstellt)`,
          artist: this.args.model.artists?.[0],
          shops: this.args.model.shops,
          cmsState: this.args.model.cmsState,
          cmsLivedate: this.args.model.cmsLivedate,
          cmsKilldate: this.args.model.cmsKilldate,
          seoTitle: this.args.model.seoTitle,
          seoDescription: this.args.model.seoDescription,
        });

        let startPage = this.store.createRecord('mlp-page', {
          name: 'Start',
          link: '/',
          start: true,
          visible: false,
        });

        mlp.pages.push(startPage);

        this.args.model.storyContents.forEach((storyContent) => {
          let contentItem = this.store.createRecord('content-item', {
            type: storyContent.type,
            collectionType:
              storyContent.type === 'collection' ? storyContent.subType : '',
            headline: storyContent.headline,
            contentItemId: storyContent.id,
          });

          startPage.contentItems.push(contentItem);
        });

        await mlp.save();

        // TODO: Get rid of it. We need to remove the new content. After createing a new content item, the content item from the response is not mapped with the new page in ember-data. https://github.com/emberjs/data/issues/1829
        mlp.pages.forEach((page) => {
          let contentItems = page.get('contentItems');
          let newContentItems = this.findNewContentItems(contentItems);

          newContentItems.map((item) => item.deleteRecord());
        });

        // TODO: Get rid of it. We need to remove the new pages. After createing a new page, the page from the response is not mapped with the new page in ember-data. https://github.com/emberjs/data/issues/1829
        let newPages = mlp.pages.filter((page) => page.get('isNew'));
        newPages.map((item) => item.deleteRecord());

        this.flashMessages.success('record_detail.forms.story.mlp_created');
      }
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  });

  findNewContentItems(contentItems) {
    return contentItems.reduce((accumulator, contentItem) => {
      let children = contentItem.get('children');
      let newContentItems = this.findNewContentItems(children);

      if (contentItem.get('isNew')) {
        newContentItems.push(contentItem);
      }

      return accumulator.concat(newContentItems);
    }, []);
  }

  // Template
  <template>
    <Form @model={{@model}} @showErrors={{@showErrors}} as |form|>
      <div class='o-layout'>
        <div class='o-layout__item u-1/1'>
          <form.fieldset @titleKey='storyContents' @isVisible={{this.isParent}}>
            <Content @story={{@model}} @form={{form}} />

            {{#if this.permissonToCreateMlp}}
              <Button
                @label={{t 'record_detail.forms.story.create_mlp'}}
                @disabled={{this.createMlp.isRunning}}
                {{on 'click' (perform this.createMlp)}}
              />
            {{/if}}
          </form.fieldset>
        </div>
      </div>
      <div class='o-layout'>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.sections.variant
            @availableLanguages={{this.availableLanguages}}
          />

          <form.fieldset @titleKey='general' as |fieldset|>
            <fieldset.field
              @property='cmsState'
              @type='select-value'
              @options={{this.cmsStateOptions}}
            />

            {{#if this.isParent}}
              <fieldset.field
                @property='cmsLanguageId'
                @type='select-value'
                @options={{this.availableLanguages}}
              />
            {{/if}}

            <fieldset.field @property='headline' />
            <fieldset.field @property='text' @type='text-editor' />
          </form.fieldset>

          <form.sections.seo />
        </div>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.fieldset @titleKey='assignments' as |fieldset|>
            <fieldset.field
              @property='artists'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='ownedByChannel'
              @type='belongs-to'
              @on={{hash openNewRecord=false openRecord=false}}
            />

            <fieldset.field
              @property='channelsAdditive'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='channelsExcluded'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='channelsExclusive'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='activeShops' @isVisible={{this.isParent}}>
            <ShopList
              @model={{@model}}
              @disabled={{or this.isDisabled (cannot 'update' @model)}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='teaser' as |fieldset|>
            <fieldset.field @property='teaserHeadline' />
            <fieldset.field @property='teaserSubHeadline' />
          </form.fieldset>

          <form.sections.meta />
        </div>
      </div>
    </Form>
  </template>
}
